import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { phoneRepairData, laptopRepairData, laptopCleaningData } from '../mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  // Services state
  const [services, setServices] = useState([]);
  
  // Prices state
  const [prices, setPrices] = useState([]);
  
  // Social links state
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    telegram: '',
    tiktok: ''
  });

  // Page content state
  const [pageContent, setPageContent] = useState({
    page: 'home',
    content: {
      heroTitle: '',
      heroSubtitle: '',
      aboutText: ''
    }
  });

  // Calculator data state
  const [calculatorData, setCalculatorData] = useState({
    phone: null,
    laptop: null,
    laptop_cleaning: null
  });

  // Dynamic services state
  const [dynamicServices, setDynamicServices] = useState([]);
  const [editingDynamicService, setEditingDynamicService] = useState(null);
  const [isCreatingService, setIsCreatingService] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    icon: 'HardDrive',
    title: '',
    description: '',
    heroImage: '',
    features: [''],
    subCategories: [],
    content: {}
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Carousel state
  const [carousel, setCarousel] = useState([]);
  
  // Gallery state
  const [gallery, setGallery] = useState([]);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetails, setDayDetails] = useState(null);
  const [dayDetailsLoading, setDayDetailsLoading] = useState(false);

  // Requests state
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Landing pages state
  const [landingSlug, setLandingSlug] = useState('ps5');
  const [landingContent, setLandingContent] = useState(null);
  const [landingLoading, setLandingLoading] = useState(false);
  const [uploadingLandingVideo, setUploadingLandingVideo] = useState(false);

  // Site settings (integrations)
  const [siteSettings, setSiteSettings] = useState({ facebook_pixel_id: '' });

  // Calculator JSON editor state
  const [calcType, setCalcType] = useState('phone');
  const [calcJson, setCalcJson] = useState('');
  // Friendly phone price editor state
  const [calcObj, setCalcObj] = useState(null);
  const [calcBrand, setCalcBrand] = useState('Apple');
  const [calcService, setCalcService] = useState('Заміна батареї');

  // Site update (self-deploy) state
  const [updateFile, setUpdateFile] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
      setActiveTab('services');
      loadAllData(token);
    }
  }, []);

  const loadAllData = async (token) => {
    try {
      // Load services
      const servicesRes = await fetch(`${API_URL}/api/services`);
      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        setServices(servicesData);
      }

      // Load prices
      const pricesRes = await fetch(`${API_URL}/api/prices`);
      if (pricesRes.ok) {
        const pricesData = await pricesRes.json();
        setPrices(pricesData);
      }

      // Load social links
      const socialRes = await fetch(`${API_URL}/api/social-links`);
      if (socialRes.ok) {
        const socialData = await socialRes.json();
        setSocialLinks(socialData);
      }

      // Load calculator data
      const calcRes = await fetch(`${API_URL}/api/admin/calculator-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (calcRes.ok) {
        const calcData = await calcRes.json();
        setCalculatorData(calcData);
      }

      // Load carousel
      const carouselRes = await fetch(`${API_URL}/api/carousel`);
      if (carouselRes.ok) {
        const carouselData = await carouselRes.json();
        setCarousel(carouselData.length > 0 ? carouselData : []);
      }

      // Load gallery
      const galleryRes = await fetch(`${API_URL}/api/gallery`);
      if (galleryRes.ok) {
        const galleryData = await galleryRes.json();
        setGallery(galleryData.length > 0 ? galleryData : []);
      }

      // Load site settings
      const settingsRes = await fetch(`${API_URL}/api/settings`);
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSiteSettings({ facebook_pixel_id: settingsData.facebook_pixel_id || '' });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
        setActiveTab('services');
        toast.success('Успешный вход!');
        loadAllData(data.token);
      } else {
        toast.error('Неверные учетные данные');
      }
    } catch (error) {
      toast.error('Ошибка входа');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setActiveTab('login');
    toast.success('Вы вышли из системы');
  };

  const handleUpdateServices = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/services`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(services)
      });

      if (response.ok) {
        toast.success('Услуги обновлены!');
      } else {
        toast.error('Ошибка обновления услуг');
      }
    } catch (error) {
      toast.error('Ошибка обновления услуг');
      console.error('Error updating services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrices = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/prices`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(prices)
      });

      if (response.ok) {
        toast.success('Цены обновлены!');
      } else {
        toast.error('Ошибка обновления цен');
      }
    } catch (error) {
      toast.error('Ошибка обновления цен');
      console.error('Error updating prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSocialLinks = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/social-links`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(socialLinks)
      });

      if (response.ok) {
        toast.success('Социальные ссылки обновлены!');
      } else {
        toast.error('Ошибка обновления ссылок');
      }
    } catch (error) {
      toast.error('Ошибка обновления ссылок');
      console.error('Error updating social links:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPageContent = async (page) => {
    try {
      const res = await fetch(`${API_URL}/api/page-content/${page}`);
      if (res.ok) {
        const data = await res.json();
        setPageContent({ page, content: data.content || {} });
      } else {
        setPageContent({ page, content: {} });
      }
    } catch (e) {
      setPageContent({ page, content: {} });
    }
  };

  const handleUpdatePageContent = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/page-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(pageContent)
      });

      if (response.ok) {
        toast.success('Контент страницы обновлен!');
      } else {
        toast.error('Ошибка обновления контента');
      }
    } catch (error) {
      toast.error('Ошибка обновления контента');
      console.error('Error updating page content:', error);
    } finally {
      setLoading(false);
    }
  };

  const addService = () => {
    const newId = services.length > 0 ? Math.max(...services.map(s => s.id)) + 1 : 1;
    setServices([...services, {
      id: newId,
      icon: '🔧',
      title: 'Новая услуга',
      description: 'Описание услуги',
      features: ['Особенность 1', 'Особенность 2'],
      link: '',
      image: ''
    }]);
  };

  const handleImageUpload = async (file, index) => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const imageUrl = `${API_URL}${data.url}`;
        updateService(index, 'image', imageUrl);
        toast.success('Изображение загружено!');
      } else {
        toast.error('Ошибка загрузки изображения');
      }
    } catch (error) {
      toast.error('Ошибка загрузки изображения');
      console.error('Error uploading image:', error);
    }
  };

  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const deleteService = (index) => {
    const updated = services.filter((_, i) => i !== index);
    setServices(updated);
  };

  const addPrice = () => {
    const newId = prices.length > 0 ? Math.max(...prices.map(p => p.id)) + 1 : 1;
    setPrices([...prices, {
      id: newId,
      service: 'Новая услуга',
      price: '0 грн'
    }]);
  };

  const updatePrice = (index, field, value) => {
    const updated = [...prices];
    updated[index][field] = value;
    setPrices(updated);
  };

  const deletePrice = (index) => {
    const updated = prices.filter((_, i) => i !== index);
    setPrices(updated);
  };

  // Carousel functions
  const addCarouselSlide = () => {
    const newId = carousel.length > 0 ? Math.max(...carousel.map(s => s.id)) + 1 : 1;
    setCarousel([...carousel, {
      id: newId,
      title: 'Новий слайд',
      description: 'Опис слайду',
      image: '',
      link: '#',
      buttonText: 'Детальніше'
    }]);
  };

  const updateCarouselSlide = (index, field, value) => {
    const updated = [...carousel];
    updated[index][field] = value;
    setCarousel(updated);
  };

  const deleteCarouselSlide = (index) => {
    setCarousel(carousel.filter((_, i) => i !== index));
  };

  const handleUpdateCarousel = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/carousel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(carousel)
      });

      if (response.ok) {
        toast.success('Карусель оновлено!');
      } else {
        toast.error('Помилка оновлення карусель');
      }
    } catch (error) {
      toast.error('Помилка оновлення карусель');
      console.error('Error updating carousel:', error);
    } finally {
      setLoading(false);
    }
  };

  // Gallery functions
  const addGalleryImage = () => {
    const newId = gallery.length > 0 ? Math.max(...gallery.map(i => i.id)) + 1 : 1;
    setGallery([...gallery, {
      id: newId,
      url: '',
      title: 'Нове зображення',
      category: 'work',
      link: ''
    }]);
  };

  const updateGalleryImage = (index, field, value) => {
    const updated = [...gallery];
    updated[index][field] = value;
    setGallery(updated);
  };

  const deleteGalleryImage = (index) => {
    setGallery(gallery.filter((_, i) => i !== index));
  };

  const handleUpdateGallery = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gallery`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(gallery)
      });

      if (response.ok) {
        toast.success('Галерею оновлено!');
      } else {
        toast.error('Помилка оновлення галереї');
      }
    } catch (error) {
      toast.error('Помилка оновлення галереї');
      console.error('Error updating gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  // Analytics functions
  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/stats?days=${analyticsDays}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        toast.error('Помилка завантаження аналітики');
      }
    } catch (error) {
      toast.error('Помилка завантаження аналітики');
      console.error('Error loading analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadDayDetails = async (date) => {
    setDayDetailsLoading(true);
    setSelectedDay(date);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/day/${date}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDayDetails(data);
      } else {
        toast.error('Помилка завантаження деталей');
      }
    } catch (error) {
      toast.error('Помилка завантаження деталей');
      console.error('Error loading day details:', error);
    } finally {
      setDayDetailsLoading(false);
    }
  };

  // Requests functions
  const loadRequests = async () => {
    setRequestsLoading(true);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else {
        toast.error('Помилка завантаження заявок');
      }
    } catch (error) {
      toast.error('Помилка завантаження заявок');
      console.error('Error loading requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const updateRequestStatus = async (timestamp, status) => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/requests/${timestamp}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success('Статус оновлено!');
        loadRequests();
      } else {
        toast.error('Помилка оновлення статусу');
      }
    } catch (error) {
      toast.error('Помилка оновлення статусу');
      console.error('Error updating status:', error);
    }
  };

  // Landing pages functions
  const loadLanding = async (slug) => {
    setLandingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/landing/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setLandingContent(data.content || {});
      } else {
        toast.error('Помилка завантаження лендингу');
      }
    } catch (e) {
      toast.error('Помилка завантаження лендингу');
    } finally {
      setLandingLoading(false);
    }
  };

  const updateLandingField = (field, value) => {
    setLandingContent((prev) => ({ ...prev, [field]: value }));
  };

  const endExpiredAdminSession = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    toast.error('Сесія завершилася. Увійдіть в адмінку ще раз.');
  };

  const handleUpdateLanding = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/landing/${landingSlug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: landingContent })
      });
      if (res.ok) {
        toast.success('Лендинг збережено!');
      } else if (res.status === 401) {
        endExpiredAdminSession();
      } else {
        toast.error('Помилка збереження лендингу');
      }
    } catch (e) {
      toast.error('Помилка збереження лендингу');
    } finally {
      setLoading(false);
    }
  };

  const handleLandingImageUpload = async (file) => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        const imageUrl = `${API_URL}${data.url}`;
        setLandingContent((prev) => ({ ...prev, photos: [...(prev.photos || []), imageUrl] }));
        toast.success('Фото додано!');
      } else {
        toast.error('Помилка завантаження фото');
      }
    } catch (e) {
      toast.error('Помилка завантаження фото');
    }
  };

  const handleLandingVideoUpload = async (file) => {
    if (!file) {
      return;
    }

    if (file.type !== 'video/mp4' || !file.name.toLowerCase().endsWith('.mp4')) {
      toast.error('Оберіть MP4-відео.');
      return;
    }

    if (file.size > 250 * 1024 * 1024) {
      toast.error('Розмір відео не може перевищувати 250 МБ.');
      return;
    }

    setUploadingLandingVideo(true);
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/admin/upload-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();

      if (res.status === 401) {
        endExpiredAdminSession();
        return;
      }

      if (!res.ok) {
        throw new Error(data.detail || 'Помилка завантаження відео');
      }

      updateLandingField('uploaded_video', `${API_URL}${data.url}`);
      toast.success('Відео завантажено. Натисніть «Зберегти лендинг».');
    } catch (error) {
      toast.error(error.message || 'Помилка завантаження відео');
    } finally {
      setUploadingLandingVideo(false);
    }
  };

  // Calculator editor functions
  const calcMockByType = { phone: phoneRepairData, laptop: laptopRepairData, 'laptop-cleaning': laptopCleaningData };

  const loadCalcEditor = (type) => {
    const dbData = type === 'phone' ? calculatorData.phone : type === 'laptop' ? calculatorData.laptop : calculatorData.laptop_cleaning;
    const data = (dbData && Object.keys(dbData).length) ? dbData : calcMockByType[type];
    setCalcType(type);
    setCalcJson(JSON.stringify(data, null, 2));
    setCalcObj(data);
    if (type === 'phone' && data) {
      const brands = Object.keys(data);
      const b = brands.includes('Apple') ? 'Apple' : brands[0];
      setCalcBrand(b);
      const svcs = b && data[b]?.repairs ? Object.keys(data[b].repairs) : [];
      setCalcService(svcs.includes('Заміна батареї') ? 'Заміна батареї' : (svcs[0] || ''));
    }
  };

  // Detect if a service uses original/copy objects or flat numbers
  const serviceMode = (brand, service) => {
    const rep = calcObj?.[brand]?.repairs?.[service] || {};
    for (const v of Object.values(rep)) {
      if (v && typeof v === 'object') return 'dual';
      if (typeof v === 'number') return 'flat';
    }
    return 'dual';
  };

  const setCalcPrice = (brand, service, model, tier, value) => {
    setCalcObj((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      if (!next[brand]) next[brand] = { models: [], repairs: {} };
      if (!next[brand].repairs[service]) next[brand].repairs[service] = {};
      const num = value === '' ? undefined : Number(value);
      if (tier === 'flat') {
        if (num === undefined) delete next[brand].repairs[service][model];
        else next[brand].repairs[service][model] = num;
      } else {
        const cur = next[brand].repairs[service][model];
        const obj = (cur && typeof cur === 'object') ? { ...cur } : {};
        if (num === undefined) delete obj[tier]; else obj[tier] = num;
        next[brand].repairs[service][model] = obj;
      }
      setCalcJson(JSON.stringify(next, null, 2));
      return next;
    });
  };

  const addDynamikService = () => {
    const label = 'Заміна динаміка';
    setCalcObj((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const brand = calcBrand;
      if (!next[brand]) return prev;
      const price = (m) => {
        if (/15\s*pro/i.test(m)) return 2400;
        if (/\b(14|15)\b/.test(m)) return 2000;
        if (/\b(12|13)\b/.test(m)) return 1800;
        return 1500;
      };
      const map = {};
      (next[brand].models || []).forEach((m) => { map[m] = price(m); });
      next[brand].repairs[label] = map;
      setCalcJson(JSON.stringify(next, null, 2));
      setCalcService(label);
      return next;
    });
    toast.success('Послугу «Заміна динаміка» додано — відредагуйте ціни та збережіть');
  };

  const handleSaveCalculator = async () => {
    let parsed;
    try {
      parsed = JSON.parse(calcJson);
    } catch (e) {
      toast.error('Помилка у форматі JSON: ' + e.message);
      return;
    }
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/calculator-data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type: calcType, data: parsed })
      });
      if (res.ok) {
        toast.success('Дані калькулятора збережено!');
      } else {
        toast.error('Помилка збереження');
      }
    } catch (e) {
      toast.error('Помилка збереження');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(siteSettings)
      });
      if (res.ok) {
        toast.success('Налаштування збережено!');
      } else {
        toast.error('Помилка збереження налаштувань');
      }
    } catch (e) {
      toast.error('Помилка збереження налаштувань');
    } finally {
      setLoading(false);
    }
  };

  // Site update (self-deploy) functions
  const startUpdatePolling = () => {
    const token = localStorage.getItem('adminToken');
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/update/status`, { headers: { 'Authorization': `Bearer ${token}` } });
        const d = await r.json();
        setUpdateStatus(d);
        if (d.state === 'success' || d.state === 'error') {
          clearInterval(id);
          setUpdateBusy(false);
          if (d.state === 'success') toast.success('Оновлення завершено!');
          else toast.error('Помилка: ' + (d.error || 'див. журнал'));
        }
      } catch (e) {
        // backend may be restarting during update — keep polling
      }
    }, 2500);
  };

  const handleSiteUpdate = async () => {
    if (!updateFile) { toast.error('Оберіть ZIP-архів'); return; }
    if (!updateFile.name.toLowerCase().endsWith('.zip')) { toast.error('Потрібен файл .zip'); return; }
    setUpdateBusy(true);
    setUpdateProgress(0);
    setUpdateStatus({ state: 'running', step: 'upload', logs: ['Завантаження архіву...'] });
    const token = localStorage.getItem('adminToken');
    const uploadId = 'up_' + Date.now();
    const CHUNK = 2 * 1024 * 1024;
    const total = Math.ceil(updateFile.size / CHUNK) || 1;
    try {
      for (let i = 0; i < total; i++) {
        const chunk = updateFile.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        fd.append('file', chunk, 'chunk');
        const r = await fetch(`${API_URL}/api/admin/update/upload`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
        });
        if (!r.ok) throw new Error('Помилка завантаження частини ' + (i + 1));
        setUpdateProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success('Архів завантажено. Застосовуємо...');
      const ar = await fetch(`${API_URL}/api/admin/update/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ upload_id: uploadId })
      });
      const ad = await ar.json();
      if (!ar.ok) throw new Error(ad.detail || 'Помилка застосування оновлення');
      startUpdatePolling();
    } catch (e) {
      toast.error(e.message);
      setUpdateBusy(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm('Відкотити сайт до попередньої версії з резервної копії?')) return;
    setUpdateBusy(true);
    setUpdateStatus({ state: 'running', step: 'rollback', logs: ['Запуск відкату...'] });
    const token = localStorage.getItem('adminToken');
    try {
      const r = await fetch(`${API_URL}/api/admin/update/rollback`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error('Помилка запуску відкату');
      toast.success('Відкат запущено');
      startUpdatePolling();
    } catch (e) {
      toast.error(e.message);
      setUpdateBusy(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md border border-white/20">
          <h1 className="text-3xl font-bold text-white mb-6 text-center">
            Админ панель CIMM
          </h1>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Логин
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Введите логин"
                required
                data-testid="admin-login-username"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Пароль
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Введите пароль"
                required
                data-testid="admin-login-password"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loading ? 'Вход...' : 'Войти'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Панель управления CIMM</h1>
              <p className="text-gray-300 mt-1">Управление контентом сайта</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all"
              >
                На сайт
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
              >
                Выйти
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 mb-6">
          <div className="flex gap-2 p-2 overflow-x-auto">
            {['services', 'prices', 'carousel', 'gallery', 'landing', 'requests', 'analytics', 'calculator', 'social', 'content', 'settings', 'update'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === 'analytics' && !analytics) {
                    loadAnalytics();
                  }
                  if (tab === 'requests' && requests.length === 0) {
                    loadRequests();
                  }
                  if (tab === 'landing' && !landingContent) {
                    loadLanding(landingSlug);
                  }
                  if (tab === 'calculator' && !calcJson) {
                    loadCalcEditor(calcType);
                  }
                  if (tab === 'content') {
                    loadPageContent(pageContent.page);
                  }
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === tab
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
                data-testid={`admin-${tab}-tab`}
              >
                {tab === 'services' && 'Услуги'}
                {tab === 'prices' && 'Цены'}
                {tab === 'carousel' && 'Карусель'}
                {tab === 'gallery' && 'Галерея'}
                {tab === 'landing' && 'Лендинги'}
                {tab === 'requests' && 'Заявки'}
                {tab === 'analytics' && 'Аналітика'}
                {tab === 'calculator' && 'Калькулятор'}
                {tab === 'social' && 'Соц. сети'}
                {tab === 'content' && 'Контент'}
                {tab === 'settings' && 'Інтеграції'}
                {tab === 'update' && 'Оновлення сайту'}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Управление услугами</h2>
                <button
                  onClick={addService}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                >
                  + Добавить услугу
                </button>
              </div>

              <div className="space-y-4">
                {services.map((service, index) => (
                  <div key={service.id} className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Иконка</label>
                        <input
                          type="text"
                          value={service.icon}
                          onChange={(e) => updateService(index, 'icon', e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Название</label>
                        <input
                          type="text"
                          value={service.title}
                          onChange={(e) => updateService(index, 'title', e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-200 mb-2">Описание</label>
                      <textarea
                        value={service.description}
                        onChange={(e) => updateService(index, 'description', e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        rows="3"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Ссылка на страницу</label>
                        <input
                          type="text"
                          value={service.link || ''}
                          onChange={(e) => updateService(index, 'link', e.target.value)}
                          placeholder="/laptops или /phones"
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">Куда ведет кнопка услуги (например: /laptops, /phones)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">URL изображения</label>
                        <input
                          type="text"
                          value={service.image || ''}
                          onChange={(e) => updateService(index, 'image', e.target.value)}
                          placeholder="https://... или оставьте пустым"
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">Или загрузите изображение ниже</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-200 mb-2">Загрузить изображение</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleImageUpload(e.target.files[0], index);
                          }
                        }}
                        className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-500 file:text-white hover:file:bg-orange-600"
                      />
                      {service.image && (
                        <div className="mt-2">
                          <img src={service.image} alt="Preview" className="h-20 w-20 object-cover rounded-lg" />
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => deleteService(index)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleUpdateServices}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить услуги'}
              </button>
            </div>
          )}

          {/* Prices Tab */}
          {activeTab === 'prices' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Управление ценами</h2>
                <button
                  onClick={addPrice}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                >
                  + Добавить цену
                </button>
              </div>

              <div className="space-y-4">
                {prices.map((price, index) => (
                  <div key={price.id} className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Услуга</label>
                        <input
                          type="text"
                          value={price.service}
                          onChange={(e) => updatePrice(index, 'service', e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-200 mb-2">Цена</label>
                          <input
                            type="text"
                            value={price.price}
                            onChange={(e) => updatePrice(index, 'price', e.target.value)}
                            className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => deletePrice(index)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleUpdatePrices}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить цены'}
              </button>
            </div>
          )}

          {/* Carousel Tab */}
          {activeTab === 'carousel' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Управление каруселью</h2>
                <button
                  onClick={addCarouselSlide}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                >
                  + Добавить слайд
                </button>
              </div>

              <div className="space-y-4">
                {carousel.map((slide, index) => (
                  <div key={slide.id} className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Заголовок</label>
                        <input
                          type="text"
                          value={slide.title}
                          onChange={(e) => updateCarouselSlide(index, 'title', e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Ссылка</label>
                        <input
                          type="text"
                          value={slide.link}
                          onChange={(e) => updateCarouselSlide(index, 'link', e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-200 mb-2">Описание</label>
                      <textarea
                        value={slide.description}
                        onChange={(e) => updateCarouselSlide(index, 'description', e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        rows="2"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">URL изображения</label>
                        <input
                          type="text"
                          value={slide.image}
                          onChange={(e) => updateCarouselSlide(index, 'image', e.target.value)}
                          placeholder="https://..."
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-2">Текст кнопки</label>
                        <input
                          type="text"
                          value={slide.buttonText}
                          onChange={(e) => updateCarouselSlide(index, 'buttonText', e.target.value)}
                          className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                    </div>
                    {slide.image && (
                      <div className="mb-4">
                        <img src={slide.image} alt="Preview" className="h-32 w-auto object-cover rounded-lg" />
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button
                        onClick={() => deleteCarouselSlide(index)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleUpdateCarousel}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить карусель'}
              </button>
            </div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Управление галереей</h2>
                <button
                  onClick={addGalleryImage}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                >
                  + Добавить изображение
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gallery.map((image, index) => (
                  <div key={image.id} className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-200 mb-2">Название</label>
                      <input
                        type="text"
                        value={image.title || ''}
                        onChange={(e) => updateGalleryImage(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-200 mb-2">URL изображения</label>
                      <input
                        type="text"
                        value={image.url}
                        onChange={(e) => updateGalleryImage(index, 'url', e.target.value)}
                        placeholder="https://..."
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-200 mb-2">Ссылка при клике (опционально)</label>
                      <input
                        type="text"
                        value={image.link || ''}
                        onChange={(e) => updateGalleryImage(index, 'link', e.target.value)}
                        placeholder="/laptops или https://..."
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    {image.url && (
                      <div className="mb-3">
                        <img src={image.url} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                      </div>
                    )}
                    <button
                      onClick={() => deleteGalleryImage(index)}
                      className="w-full px-3 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-all"
                    >
                      Удалить
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={handleUpdateGallery}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить галерею'}
              </button>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Статистика відвідувань</h2>
                <div className="flex gap-2">
                  <select
                    value={analyticsDays}
                    onChange={(e) => setAnalyticsDays(Number(e.target.value))}
                    className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                  >
                    <option value={7}>7 днів</option>
                    <option value={30}>30 днів</option>
                    <option value={90}>90 днів</option>
                  </select>
                  <button
                    onClick={loadAnalytics}
                    disabled={analyticsLoading}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50"
                  >
                    {analyticsLoading ? 'Завантаження...' : 'Оновити'}
                  </button>
                </div>
              </div>

              {analytics && (
                <>
                  {/* Total Visits */}
                  <div className="bg-gradient-to-r from-orange-500/20 to-orange-600/20 p-6 rounded-xl border border-orange-500/30">
                    <h3 className="text-lg text-gray-300 mb-2">Всього відвідувань</h3>
                    <p className="text-5xl font-bold text-orange-500">{analytics.total_visits}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Top Pages */}
                    <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                      <h3 className="text-xl font-bold text-white mb-4">Популярні сторінки</h3>
                      <div className="space-y-3">
                        {analytics.top_pages.map((item, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-gray-300">{item.page}</span>
                            <span className="text-orange-500 font-semibold">{item.visits}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Referrers */}
                    <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                      <h3 className="text-xl font-bold text-white mb-4">Звідки приходять</h3>
                      <div className="space-y-3">
                        {analytics.top_referrers.map((item, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-gray-300">{item.source}</span>
                            <span className="text-orange-500 font-semibold">{item.visits}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Top Cities */}
                  {analytics.top_cities.length > 0 && (
                    <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                      <h3 className="text-xl font-bold text-white mb-4">Міста відвідувачів</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        {analytics.top_cities.map((item, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <span className="text-gray-300">{item.city}</span>
                            <span className="text-orange-500 font-semibold">{item.visits}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Daily Visits Chart */}
                  <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-4">Відвідування по днях (клікніть для деталей)</h3>
                    <div className="space-y-2">
                      {analytics.daily_visits.slice(-14).map((item, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm w-24">{item.date}</span>
                          <div 
                            className="flex-1 bg-white/10 rounded-full h-6 relative cursor-pointer hover:bg-white/20 transition-all"
                            onClick={() => loadDayDetails(item.date)}
                          >
                            <div 
                              className="bg-orange-500 h-full rounded-full transition-all"
                              style={{width: `${(item.visits / Math.max(...analytics.daily_visits.map(d => d.visits))) * 100}%`}}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white font-semibold">
                              {item.visits}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Day Details Modal */}
                  {selectedDay && (
                    <div className="bg-white/5 p-6 rounded-xl border border-white/10 mt-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-white">Деталі за {selectedDay}</h3>
                        <button
                          onClick={() => { setSelectedDay(null); setDayDetails(null); }}
                          className="text-gray-400 hover:text-white"
                        >
                          ✕
                        </button>
                      </div>

                      {dayDetailsLoading ? (
                        <div className="text-center text-gray-400 py-8">Завантаження...</div>
                      ) : dayDetails ? (
                        <div className="grid md:grid-cols-3 gap-6">
                          {/* Pages for the day */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3">Сторінки</h4>
                            <div className="space-y-2">
                              {dayDetails.pages.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-gray-400">{item.page}</span>
                                  <span className="text-orange-500 font-semibold">{item.visits}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Referrers for the day */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3">Джерела</h4>
                            <div className="space-y-2">
                              {dayDetails.referrers.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-gray-400 truncate">{item.source}</span>
                                  <span className="text-green-500 font-semibold">{item.visits}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Cities for the day */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3">Міста</h4>
                            <div className="space-y-2">
                              {dayDetails.cities.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-gray-400">{item.city}</span>
                                  <span className="text-blue-500 font-semibold">{item.visits}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}

              {!analytics && !analyticsLoading && (
                <div className="text-center text-gray-400 py-12">
                  <p>Натисніть "Оновити" щоб завантажити статистику</p>
                </div>
              )}
            </div>
          )}

          {/* Landing Pages Tab */}
          {activeTab === 'landing' && (
            <div className="space-y-6">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <h2 className="text-2xl font-bold text-white">Редактор лендингів (чистка)</h2>
                <select
                  value={landingSlug}
                  onChange={(e) => {
                    const slug = e.target.value;
                    setLandingSlug(slug);
                    setLandingContent(null);
                    loadLanding(slug);
                  }}
                  className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                  data-testid="landing-select"
                >
                  <option value="ps5">Чистка PlayStation 5</option>
                  <option value="laptops">Чистка ноутбуків</option>
                  <option value="macbook">Обслуговування MacBook</option>
                  <option value="phones">Ремонт телефонів</option>
                  <option value="pc">Чистка ПК</option>
                </select>
              </div>

              {landingLoading || !landingContent ? (
                <div className="text-center text-gray-400 py-12">Завантаження...</div>
              ) : (
                <div className="space-y-6">
                  {/* SEO */}
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                    <h3 className="text-lg font-semibold text-orange-400">SEO (пошукові системи)</h3>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Заголовок сторінки (title)</label>
                      <input type="text" value={landingContent.metaTitle || ''} onChange={(e) => updateLandingField('metaTitle', e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" data-testid="landing-meta-title" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Опис (description)</label>
                      <textarea value={landingContent.metaDescription || ''} onChange={(e) => updateLandingField('metaDescription', e.target.value)} rows="2" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Ключові слова (keywords, через кому)</label>
                      <textarea value={landingContent.metaKeywords || ''} onChange={(e) => updateLandingField('metaKeywords', e.target.value)} rows="2" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                  </div>

                  {/* Hero */}
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                    <h3 className="text-lg font-semibold text-orange-400">Головний блок</h3>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Заголовок</label>
                      <input type="text" value={landingContent.heroTitle || ''} onChange={(e) => updateLandingField('heroTitle', e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Підзаголовок</label>
                      <textarea value={landingContent.heroSubtitle || ''} onChange={(e) => updateLandingField('heroSubtitle', e.target.value)} rows="2" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">URL фонового зображення</label>
                      <input type="text" value={landingContent.heroImage || ''} onChange={(e) => updateLandingField('heroImage', e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                  </div>

                  {/* Intro */}
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                    <h3 className="text-lg font-semibold text-orange-400">Інформація</h3>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Заголовок блоку</label>
                      <input type="text" value={landingContent.introTitle || ''} onChange={(e) => updateLandingField('introTitle', e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Текст</label>
                      <textarea value={landingContent.introText || ''} onChange={(e) => updateLandingField('introText', e.target.value)} rows="4" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Ознаки, що потрібна чистка (по одному в рядку)</label>
                      <textarea value={(landingContent.signs || []).join('\n')} onChange={(e) => updateLandingField('signs', e.target.value.split('\n').filter(Boolean))} rows="5" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Що входить у послугу (по одному в рядку)</label>
                      <textarea value={(landingContent.included || []).join('\n')} onChange={(e) => updateLandingField('included', e.target.value.split('\n').filter(Boolean))} rows="5" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                  </div>

                  {/* Videos */}
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                    <h3 className="text-lg font-semibold text-orange-400">Відео з Instagram</h3>
                    <p className="text-xs text-gray-400">Вставте повне посилання на Reels, пост або відео Instagram.</p>
                    {landingSlug === 'laptops' ? (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="block text-sm text-gray-300 mb-1">Звичайні ноутбуки</label>
                          <input
                            type="url"
                            value={landingContent.instagram_video_regular || ''}
                            onChange={(e) => updateLandingField('instagram_video_regular', e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                            data-testid="landing-regular-video-input"
                            placeholder="https://www.instagram.com/reel/..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-300 mb-1">Ігрові ноутбуки</label>
                          <input
                            type="url"
                            value={landingContent.instagram_video_gaming || ''}
                            onChange={(e) => updateLandingField('instagram_video_gaming', e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                            data-testid="landing-gaming-video-input"
                            placeholder="https://www.instagram.com/reel/..."
                          />
                        </div>
                      </div>
                    ) : (
                      <input
                        type="url"
                        value={landingContent.instagram_video || ''}
                        onChange={(e) => updateLandingField('instagram_video', e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        data-testid="landing-video-input"
                        placeholder="https://www.instagram.com/reel/..."
                      />
                    )}
                    {landingSlug === 'ps5' && (
                      <div className="border-t border-white/10 pt-4">
                        <label className="mb-1 block text-sm text-gray-300">
                          Основне MP4-відео на сторінці
                        </label>
                        <label className="inline-flex cursor-pointer items-center rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-600">
                          {uploadingLandingVideo ? 'Завантаження відео...' : 'Обрати MP4-відео'}
                          <input
                            type="file"
                            accept="video/mp4"
                            disabled={uploadingLandingVideo}
                            onChange={(e) => handleLandingVideoUpload(e.target.files?.[0])}
                            className="sr-only"
                            data-testid="landing-video-file-input"
                          />
                        </label>
                        <p className="mt-2 text-xs text-gray-400">
                          MP4 до 250 МБ. Після завантаження натисніть «Зберегти лендинг».
                        </p>
                        {landingContent.uploaded_video && (
                          <p className="mt-2 text-xs text-green-400" data-testid="uploaded-video-status">
                            Основне відео готове до показу на сторінці.
                          </p>
                        )}
                        <label className="mt-4 mb-1 block text-sm text-gray-300">
                          Instagram-пости внизу сторінки, по одному в рядку
                        </label>
                        <textarea
                          value={(landingContent.instagram_posts || []).join('\n')}
                          onChange={(e) => updateLandingField('instagram_posts', e.target.value.split('\n').filter(Boolean))}
                          rows="3"
                          className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                          data-testid="landing-instagram-posts-input"
                          placeholder="https://www.instagram.com/reel/..."
                        />
                      </div>
                    )}
                  </div>

                  {/* Photos */}
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                    <h3 className="text-lg font-semibold text-orange-400">Фото</h3>
                    <label className="block text-sm text-gray-300 mb-1">Завантажити фото</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => { if (e.target.files && e.target.files[0]) handleLandingImageUpload(e.target.files[0]); }}
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-500 file:text-white hover:file:bg-orange-600"
                    />
                    <label className="block text-sm text-gray-300 mb-1">Або вставте URL фото (по одному в рядку)</label>
                    <textarea value={(landingContent.photos || []).join('\n')} onChange={(e) => updateLandingField('photos', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))} rows="4" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    {(landingContent.photos || []).length > 0 && (
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        {landingContent.photos.map((p, i) => (
                          <img key={i} src={p} alt={`p-${i}`} className="w-full h-20 object-cover rounded-lg" />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Price + CTA */}
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                    <h3 className="text-lg font-semibold text-orange-400">Ціна та заклик</h3>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Текст про ціну</label>
                      <textarea value={landingContent.priceText || ''} onChange={(e) => updateLandingField('priceText', e.target.value)} rows="2" className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">Заголовок заклику (CTA)</label>
                      <input type="text" value={landingContent.ctaTitle || ''} onChange={(e) => updateLandingField('ctaTitle', e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" />
                    </div>
                    <label className="flex items-center gap-2 text-gray-300">
                      <input type="checkbox" checked={!!landingContent.showCalculator} onChange={(e) => updateLandingField('showCalculator', e.target.checked)} className="w-4 h-4" />
                      Показувати калькулятор чистки ноутбуків
                    </label>
                  </div>

                  <button
                    onClick={handleUpdateLanding}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
                    data-testid="landing-save-btn"
                  >
                    {loading ? 'Збереження...' : 'Зберегти лендинг'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Заявки з сайту</h2>
                <button
                  onClick={loadRequests}
                  disabled={requestsLoading}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50"
                >
                  {requestsLoading ? 'Завантаження...' : 'Оновити'}
                </button>
              </div>

              {requests.length > 0 ? (
                <div className="space-y-4">
                  {requests.map((request, index) => (
                    <div 
                      key={index}
                      className={`bg-white/5 p-6 rounded-xl border ${
                        request.status === 'new' ? 'border-orange-500/50' : 'border-white/10'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            request.type === 'booking' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {request.type === 'booking' ? 'Запис на ремонт' : 'Контактна форма'}
                          </span>
                          <span className={`ml-3 px-3 py-1 rounded-full text-sm font-semibold ${
                            request.status === 'new' 
                              ? 'bg-orange-500/20 text-orange-400' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {request.status === 'new' ? 'Нова' : 'Оброблена'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {new Date(request.timestamp).toLocaleString('uk-UA')}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-sm mb-1">Ім'я:</p>
                          <p className="text-white font-semibold">{request.name}</p>
                        </div>
                        
                        {request.type === 'booking' ? (
                          <>
                            <div>
                              <p className="text-gray-400 text-sm mb-1">Телефон:</p>
                              <p className="text-white font-semibold">
                                <a href={`tel:${request.phone}`} className="hover:text-orange-500">
                                  {request.phone}
                                </a>
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-sm mb-1">Пристрій:</p>
                              <p className="text-white font-semibold">{request.device}</p>
                            </div>
                            <div className="md:col-span-2">
                              <p className="text-gray-400 text-sm mb-1">Проблема:</p>
                              <p className="text-white">{request.issue}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-gray-400 text-sm mb-1">Email:</p>
                              <p className="text-white font-semibold">
                                <a href={`mailto:${request.email}`} className="hover:text-orange-500">
                                  {request.email}
                                </a>
                              </p>
                            </div>
                            <div className="md:col-span-2">
                              <p className="text-gray-400 text-sm mb-1">Повідомлення:</p>
                              <p className="text-white">{request.message}</p>
                            </div>
                          </>
                        )}
                      </div>

                      {request.status === 'new' && (
                        <div className="mt-4">
                          <button
                            onClick={() => updateRequestStatus(request.timestamp, 'processed')}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                          >
                            Позначити як оброблену
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-12">
                  <p>Заявок поки немає</p>
                </div>
              )}
            </div>
          )}

          {/* Calculator Tab */}
          {activeTab === 'calculator' && (
            <div className="space-y-6">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <h2 className="text-2xl font-bold text-white">Редактор калькулятора (ціни та моделі)</h2>
                <select
                  value={calcType}
                  onChange={(e) => loadCalcEditor(e.target.value)}
                  className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                  data-testid="calc-type-select"
                >
                  <option value="phone">Ремонт телефонів</option>
                  <option value="laptop">Ремонт ноутбуків</option>
                  <option value="laptop-cleaning">Чистка ноутбуків / MacBook</option>
                </select>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-300 text-sm">
                  <strong>Як редагувати:</strong> змінюйте ціни/моделі прямо в полі нижче (формат JSON), потім тисніть «Зберегти».
                  Дані зберігаються в базі — сайт не зламається. Кнопка «Завантажити з сайту» підставляє поточні дані.
                  Не видаляйте лапки, коми та дужки.
                </p>
              </div>

              {calcType === 'phone' && calcObj && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-4" data-testid="phone-price-editor">
                  <h3 className="text-lg font-semibold text-orange-400">Швидке редагування цін</h3>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Бренд</label>
                      <select value={calcBrand} onChange={(e) => { const b = e.target.value; setCalcBrand(b); const svcs = Object.keys(calcObj[b]?.repairs || {}); setCalcService(svcs[0] || ''); }} className="px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" data-testid="pe-brand">
                        {Object.keys(calcObj).map((b) => <option key={b} value={b} className="bg-gray-800">{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Послуга</label>
                      <select value={calcService} onChange={(e) => setCalcService(e.target.value)} className="px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white" data-testid="pe-service">
                        {Object.keys(calcObj[calcBrand]?.repairs || {}).map((s) => <option key={s} value={s} className="bg-gray-800">{s}</option>)}
                      </select>
                    </div>
                    {calcBrand === 'Apple' && !calcObj.Apple?.repairs?.['Заміна динаміка'] && (
                      <button onClick={addDynamikService} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700" data-testid="pe-add-dynamik">➕ Додати «Заміна динаміка»</button>
                    )}
                  </div>

                  <div className="max-h-[420px] overflow-y-auto pr-2 space-y-2">
                    {(calcObj[calcBrand]?.models || []).map((m) => {
                      const val = calcObj[calcBrand]?.repairs?.[calcService]?.[m];
                      const mode = serviceMode(calcBrand, calcService);
                      return (
                        <div key={m} className="flex items-center gap-3 bg-black/20 rounded-lg px-3 py-2">
                          <span className="text-white text-sm w-44 md:w-56 flex-shrink-0">{m}</span>
                          {mode === 'flat' ? (
                            <div>
                              <span className="block text-[10px] text-gray-400">Ціна, грн</span>
                              <input type="number" value={typeof val === 'number' ? val : ''} onChange={(e) => setCalcPrice(calcBrand, calcService, m, 'flat', e.target.value)} placeholder="—" className="w-28 px-2 py-1 bg-white/5 border border-white/20 rounded text-white text-sm" data-testid={`pe-price-${m}`} />
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <div>
                                <span className="block text-[10px] text-gray-400">Оригінал</span>
                                <input type="number" value={val && typeof val === 'object' && val.original != null ? val.original : ''} onChange={(e) => setCalcPrice(calcBrand, calcService, m, 'original', e.target.value)} placeholder="—" className="w-24 px-2 py-1 bg-white/5 border border-white/20 rounded text-white text-sm" data-testid={`pe-orig-${m}`} />
                              </div>
                              <div>
                                <span className="block text-[10px] text-gray-400">Копія</span>
                                <input type="number" value={val && typeof val === 'object' && val.copy != null ? val.copy : ''} onChange={(e) => setCalcPrice(calcBrand, calcService, m, 'copy', e.target.value)} placeholder="—" className="w-24 px-2 py-1 bg-white/5 border border-white/20 rounded text-white text-sm" data-testid={`pe-copy-${m}`} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <button onClick={handleSaveCalculator} disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50" data-testid="pe-save">
                    {loading ? 'Збереження...' : '💾 Зберегти ціни'}
                  </button>
                  <p className="text-xs text-gray-400">Порожнє поле = ціна прихована. Зміни зберігаються в базу і одразу показуються на сайті. Складніші правки (додати моделі/бренди) — у JSON-редакторі нижче.</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => loadCalcEditor(calcType)}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all"
                  data-testid="calc-reload-btn"
                >
                  ↻ Завантажити поточні дані
                </button>
              </div>

              <textarea
                value={calcJson}
                onChange={(e) => setCalcJson(e.target.value)}
                rows="22"
                spellCheck="false"
                className="w-full px-4 py-3 bg-black/40 border border-white/20 rounded-lg text-white font-mono text-xs leading-relaxed"
                data-testid="calc-json-editor"
              />

              <button
                onClick={handleSaveCalculator}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
                data-testid="calc-save-btn"
              >
                {loading ? 'Збереження...' : 'Зберегти дані калькулятора'}
              </button>
            </div>
          )}

          {/* Social Links Tab */}
          {activeTab === 'social' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Социальные сети</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Instagram</label>
                  <input
                    type="text"
                    value={socialLinks.instagram}
                    onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    placeholder="https://www.instagram.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Telegram</label>
                  <input
                    type="text"
                    value={socialLinks.telegram}
                    onChange={(e) => setSocialLinks({...socialLinks, telegram: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    placeholder="https://t.me/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">TikTok</label>
                  <input
                    type="text"
                    value={socialLinks.tiktok}
                    onChange={(e) => setSocialLinks({...socialLinks, tiktok: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    placeholder="https://www.tiktok.com/@..."
                  />
                </div>
              </div>

              <button
                onClick={handleUpdateSocialLinks}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить ссылки'}
              </button>
            </div>
          )}

          {/* Settings / Integrations Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Інтеграції та відстеження</h2>

              <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                <h3 className="text-lg font-semibold text-orange-400">Facebook Pixel</h3>
                <p className="text-xs text-gray-400">
                  Введіть ID вашого Facebook Pixel (Events Manager → Налаштування даних). Це 15-16 цифр.
                  Pixel відстежує перегляди сторінок та події <strong>Lead</strong> при відправці заявок/контактів.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Facebook Pixel ID</label>
                  <input
                    type="text"
                    value={siteSettings.facebook_pixel_id || ''}
                    onChange={(e) => setSiteSettings({ ...siteSettings, facebook_pixel_id: e.target.value.trim() })}
                    placeholder="напр. 123456789012345"
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500"
                    data-testid="fb-pixel-input"
                  />
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-blue-300 text-xs">
                    💡 Після збереження Pixel почне працювати на всіх сторінках. Перевірити можна розширенням
                    «Meta Pixel Helper» у Chrome. Подія Lead спрацьовує при відправці форми заявки та контактної форми.
                  </p>
                </div>
              </div>

              <button
                onClick={handleUpdateSettings}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
                data-testid="settings-save-btn"
              >
                {loading ? 'Збереження...' : 'Зберегти налаштування'}
              </button>
            </div>
          )}

          {/* Site Update (self-deploy) Tab */}
          {activeTab === 'update' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Оновлення сайту</h2>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-200 space-y-2">
                <p><strong>Як це працює:</strong> завантажте ZIP-архів усього проєкту (експорт з GitHub — папка з <code>frontend/</code> та <code>backend/</code>). Система автоматично:</p>
                <p>1. Зробить резервну копію поточної версії → 2. Розпакує нові файли (ваші <strong>.env</strong> та завантажені фото збережуться) → 3. Збере фронтенд → 4. Перезапустить сервіси.</p>
                <p className="text-yellow-300">⚠️ Під час оновлення сайт може бути недоступним 1-3 хвилини. Не закривайте цю сторінку.</p>
              </div>

              <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-4">
                <label className="block text-sm font-medium text-gray-200">ZIP-архів проєкту</label>
                <input
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => setUpdateFile(e.target.files && e.target.files[0])}
                  disabled={updateBusy}
                  className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange-500 file:text-white hover:file:bg-orange-600 disabled:opacity-50"
                  data-testid="update-file-input"
                />
                {updateFile && <p className="text-gray-400 text-sm">Обрано: {updateFile.name} ({(updateFile.size / 1048576).toFixed(1)} МБ)</p>}

                <button
                  onClick={handleSiteUpdate}
                  disabled={updateBusy || !updateFile}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
                  data-testid="update-apply-btn"
                >
                  {updateBusy ? 'Оновлення виконується...' : 'Завантажити та оновити сайт'}
                </button>

                {updateBusy && updateStatus && updateStatus.step === 'upload' && (
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div className="bg-orange-500 h-2 rounded-full transition-all" style={{ width: `${updateProgress}%` }} />
                  </div>
                )}
              </div>

              {updateStatus && updateStatus.state && updateStatus.state !== 'idle' && (
                <div className="bg-black/40 p-4 rounded-lg border border-white/10" data-testid="update-status">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white">
                      Статус: {updateStatus.state === 'running' ? 'виконується' : updateStatus.state === 'success' ? '✅ успішно' : updateStatus.state === 'error' ? '❌ помилка' : updateStatus.state}
                    </span>
                    {typeof updateStatus.progress === 'number' && <span className="text-xs text-gray-400">{updateStatus.progress}%</span>}
                  </div>
                  {updateStatus.progress != null && (
                    <div className="w-full bg-white/10 rounded-full h-2 mb-3">
                      <div className={`h-2 rounded-full transition-all ${updateStatus.state === 'error' ? 'bg-red-500' : 'bg-orange-500'}`} style={{ width: `${updateStatus.progress}%` }} />
                    </div>
                  )}
                  {updateStatus.error && <p className="text-red-400 text-sm mb-2">{updateStatus.error}</p>}
                  <div className="bg-black/50 rounded p-2 max-h-48 overflow-y-auto font-mono text-xs text-gray-300 space-y-1">
                    {(updateStatus.logs || []).map((l, i) => <div key={i}>{l}</div>)}
                  </div>
                </div>
              )}

              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-orange-400 mb-2">Відкат (rollback)</h3>
                <p className="text-gray-400 text-sm mb-3">Якщо після оновлення щось пішло не так — поверніть попередню версію з останньої резервної копії.</p>
                <button
                  onClick={handleRollback}
                  disabled={updateBusy}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all disabled:opacity-50"
                  data-testid="update-rollback-btn"
                >
                  ↩ Відкотити до попередньої версії
                </button>
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Контент страниц</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Страница</label>
                  <select
                    value={pageContent.page}
                    onChange={(e) => loadPageContent(e.target.value)}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    data-testid="content-page-select"
                  >
                    <option value="home">Главная</option>
                    <option value="phones">Телефоны</option>
                    <option value="laptops">Ноутбуки</option>
                    <option value="contacts">Контакты</option>
                  </select>
                </div>

                <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                  <h3 className="text-lg font-semibold text-orange-400">SEO (пошук Google)</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">SEO заголовок (title)</label>
                    <input
                      type="text"
                      value={pageContent.content.metaTitle || ''}
                      onChange={(e) => setPageContent({ ...pageContent, content: { ...pageContent.content, metaTitle: e.target.value } })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      data-testid="content-meta-title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">SEO опис (description)</label>
                    <textarea
                      value={pageContent.content.metaDescription || ''}
                      onChange={(e) => setPageContent({ ...pageContent, content: { ...pageContent.content, metaDescription: e.target.value } })}
                      rows="2"
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-2">SEO ключові слова (keywords)</label>
                    <textarea
                      value={pageContent.content.metaKeywords || ''}
                      onChange={(e) => setPageContent({ ...pageContent, content: { ...pageContent.content, metaKeywords: e.target.value } })}
                      rows="2"
                      className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Заголовок Hero</label>
                  <input
                    type="text"
                    value={pageContent.content.heroTitle || ''}
                    onChange={(e) => setPageContent({
                      ...pageContent,
                      content: {...pageContent.content, heroTitle: e.target.value}
                    })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Подзаголовок</label>
                  <input
                    type="text"
                    value={pageContent.content.heroSubtitle || ''}
                    onChange={(e) => setPageContent({
                      ...pageContent,
                      content: {...pageContent.content, heroSubtitle: e.target.value}
                    })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">Текст "О нас"</label>
                  <textarea
                    value={pageContent.content.aboutText || ''}
                    onChange={(e) => setPageContent({
                      ...pageContent,
                      content: {...pageContent.content, aboutText: e.target.value}
                    })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    rows="5"
                  />
                </div>
              </div>

              <button
                onClick={handleUpdatePageContent}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить контент'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
